import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Package, MapPin, CreditCard, AlertCircle, CheckCircle2 } from "lucide-react";
import { customerApi, formatStoreError } from "@/lib/customerApi";
import { formatPrice, productUrl } from "@/lib/storefront";
import { OrderRow, humanStatus } from "./AccountOverview";

export default function AccountOrders() {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    customerApi.get("/orders")
      .then((r) => setData(r.data || { items: [] }))
      .catch((e) => setError(formatStoreError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="account-orders-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#2D2823]">Order history</div>
        <h2 className="font-display text-3xl">Your orders</h2>
      </div>

      {loading ? (
        <div className="h-40 animate-pulse rounded-md bg-[#E8E2D2]" />
      ) : error ? (
        <div className="rounded-md bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
      ) : data.items.length === 0 ? (
        <div className="rounded-md border border-[#DDD6C5] bg-white p-10 text-center" data-testid="orders-empty">
          <Package size={40} strokeWidth={1.2} className="mx-auto mb-4 text-[#2D2823]/30" />
          <h3 className="font-display text-2xl">No orders yet</h3>
          <p className="mt-1 text-sm text-[#2D2823]">Browse the shop and check back here.</p>
          <Link to="/catalog" className="mt-6 inline-flex rounded-full bg-[#1A1A1A] px-6 py-3 text-sm text-white transition-colors hover:bg-[#7D8471]">
            Start shopping
          </Link>
        </div>
      ) : (
        <div className="divide-y divide-[#DDD6C5] rounded-md border border-[#DDD6C5] bg-white">
          {data.items.map((o) => <OrderRow key={o.id} order={o} />)}
        </div>
      )}
    </div>
  );
}

export function AccountOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    customerApi.get(`/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch((e) => setError(formatStoreError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="h-72 animate-pulse rounded-md bg-[#E8E2D2]" />;
  if (error || !order) return (
    <div data-testid="order-not-found">
      <Link to="/account/orders" className="mb-6 inline-flex items-center gap-1 text-sm text-[#7D8471] hover:text-[#1A1A1A]">
        <ArrowLeft size={14} /> Back to orders
      </Link>
      <div className="rounded-md bg-rose-50 p-4 text-sm text-rose-700">{error || "Order not found"}</div>
    </div>
  );

  const failed = order.payment_status === "failed" || order.status === "payment_error";

  return (
    <div data-testid="account-order-detail">
      <Link to="/account/orders" className="mb-6 inline-flex items-center gap-1 text-sm text-[#7D8471] hover:text-[#1A1A1A]">
        <ArrowLeft size={14} /> Back to orders
      </Link>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-sm text-[#2D2823]">{order.order_number}</div>
          <h2 className="mt-1 font-display text-3xl">{humanStatus(order.status)}</h2>
          <div className="mt-1 text-xs text-[#2D2823]">Placed {order.created_at?.slice(0, 10)}</div>
        </div>
        <div className="store-nums font-display text-3xl">{formatPrice(order.total || 0, order.currency || "USD")}</div>
      </div>

      {failed && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700" data-testid="order-payment-error">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">Payment was declined</div>
            <p className="mt-0.5 text-xs">Our team will reach out to help you complete the purchase.</p>
          </div>
        </div>
      )}

      {/* Items */}
      <section className="rounded-md border border-[#DDD6C5] bg-white">
        <div className="border-b border-[#DDD6C5] px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#2D2823]">Items</div>
        <ul className="divide-y divide-[#DDD6C5]">
          {(order.items || []).map((it, i) => (
            <li key={i} className="flex items-center gap-4 px-5 py-4">
              <Link to={productUrl(it)} className="block h-16 w-12 flex-shrink-0 overflow-hidden rounded bg-[#E8E2D2]">
                {it.image && <img src={it.image} alt="" className="h-full w-full object-cover" />}
              </Link>
              <div className="min-w-0 flex-1">
                <Link to={productUrl(it)} className="text-sm font-medium text-[#1A1A1A] hover:text-[#7D8471]">{it.title}</Link>
                <div className="mt-0.5 text-xs text-[#2D2823]">
                  Qty {it.quantity} · {formatPrice(it.price, order.currency || "USD")}
                </div>
              </div>
              <div className="store-nums text-sm">{formatPrice(it.subtotal || it.price * it.quantity, order.currency || "USD")}</div>
            </li>
          ))}
        </ul>

        <dl className="space-y-1.5 border-t border-[#DDD6C5] px-5 py-4 text-sm">
          <div className="flex justify-between"><dt className="text-[#2D2823]">Subtotal</dt><dd className="store-nums">{formatPrice(order.subtotal || 0, order.currency || "USD")}</dd></div>
          {order.discount > 0 && <div className="flex justify-between text-[#7D8471]"><dt>Discount {order.promo_code ? `(${order.promo_code})` : ""}</dt><dd className="store-nums">−{formatPrice(order.discount, order.currency || "USD")}</dd></div>}
          <div className="flex justify-between"><dt className="text-[#2D2823]">Shipping</dt><dd className="store-nums">{order.shipping_cost ? formatPrice(order.shipping_cost, order.currency || "USD") : "Free"}</dd></div>
          <div className="flex justify-between border-t border-[#DDD6C5] pt-2 font-display text-base"><dt>Total</dt><dd className="store-nums">{formatPrice(order.total || 0, order.currency || "USD")}</dd></div>
        </dl>
      </section>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {order.shipping_address && (
          <section className="rounded-md border border-[#DDD6C5] bg-white p-5">
            <div className="mb-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#2D2823]">
              <MapPin size={12} /> Shipping
            </div>
            <div className="text-sm text-[#1A1A1A]">
              {order.shipping_address.recipient || order.customer_name}<br />
              {order.shipping_address.street}<br />
              {order.shipping_address.city}{order.shipping_address.region ? `, ${order.shipping_address.region}` : ""} {order.shipping_address.zip}<br />
              {order.shipping_address.country}
            </div>
            <div className="mt-2 text-xs text-[#2D2823]">{order.shipping_method || "Standard"}</div>
          </section>
        )}
        <section className="rounded-md border border-[#DDD6C5] bg-white p-5">
          <div className="mb-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#2D2823]">
            <CreditCard size={12} /> Payment
          </div>
          <div className="flex items-center gap-2 text-sm">
            {order.payment_status === "paid" ? <CheckCircle2 size={14} className="text-[#7D8471]" /> : null}
            <span className="capitalize">{(order.payment_method || "card").replace("_", " ")}</span>
            <span className="text-xs text-[#2D2823]">— {humanStatus(order.payment_status)}</span>
          </div>
        </section>
      </div>
    </div>
  );
}
